import React, { useState, useEffect } from 'react';

/**
 * Marketing Mailing List Dashboard
 * View and manage marketing contacts
 */
const MailingListDashboard = () => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    consentOnly: true,
    activeOnly: true,
    tags: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadMarketingData();
  }, []);

  const loadMarketingData = async () => {
    setLoading(true);
    try {
      // Load stats
      const statsResponse = await fetch(`${BACKEND_URL}/api/crm/marketing/stats`, {
        credentials: 'include'
      });
      const statsData = await statsResponse.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }

      // Load contacts
      await loadContacts();
    } catch (error) {
      console.error('Error loading marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    try {
      const params = new URLSearchParams({
        consent_only: filters.consentOnly,
        active_only: filters.activeOnly
      });
      
      if (filters.tags) {
        params.append('tags', filters.tags);
      }

      const response = await fetch(`${BACKEND_URL}/api/crm/marketing/contacts?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.success) {
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleSendCampaign = async () => {
    if (!window.confirm('Send test campaign? (Email service not yet connected)')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/marketing/send-campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          campaign_type: 'newsletter',
          tags: filters.tags ? filters.tags.split(',') : ['trial_user'],
          test_mode: true
        })
      });

      const data = await response.json();
      alert(`✅ ${data.message}\n\nWould send to: ${data.would_send_to} contacts\nNote: ${data.note}`);
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('❌ Failed to send campaign');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading marketing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">📧 Marketing & Mailing List</h2>
          <p className="text-gray-600 mt-1">Manage customer emails and marketing campaigns</p>
        </div>
        <button
          onClick={handleSendCampaign}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:shadow-lg transition"
        >
          📤 Send Campaign (Test)
        </button>
      </div>

      {/* Email Service Notice */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Email Service Integration Pending:</strong> Marketing emails are being captured but not sent yet. Connect your email service to start sending campaigns.
            </p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="text-3xl font-bold text-blue-600">{stats.total_contacts}</div>
            <div className="text-gray-600 text-sm mt-1">Total Contacts</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="text-3xl font-bold text-green-600">{stats.marketing_consented}</div>
            <div className="text-gray-600 text-sm mt-1">Marketing Consent</div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.consent_rate.toFixed(1)}% consent rate
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="text-3xl font-bold text-purple-600">{stats.active_emails}</div>
            <div className="text-gray-600 text-sm mt-1">Active Emails</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="text-3xl font-bold text-red-600">{stats.unsubscribed}</div>
            <div className="text-gray-600 text-sm mt-1">Unsubscribed</div>
          </div>
        </div>
      )}

      {/* Sources Breakdown */}
      {stats && stats.sources && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">📊 Signup Sources</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.sources).map(([source, count]) => (
              <div key={source} className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-sm text-gray-600 capitalize">{source.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">🔍 Filter Contacts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.consentOnly}
                onChange={(e) => setFilters({...filters, consentOnly: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700">Marketing consent only</span>
            </label>
          </div>
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.activeOnly}
                onChange={(e) => setFilters({...filters, activeOnly: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-gray-700">Active emails only</span>
            </label>
          </div>
          <div>
            <input
              type="text"
              placeholder="Tags (comma-separated)"
              value={filters.tags}
              onChange={(e) => setFilters({...filters, tags: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <button
          onClick={loadContacts}
          className="mt-4 px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
        >
          Apply Filters
        </button>
      </div>

      {/* Contacts List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          📋 Contacts ({contacts.length})
        </h3>
        
        {contacts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <div className="text-6xl mb-4">📭</div>
            <h4 className="text-xl font-semibold text-gray-700 mb-2">No contacts found</h4>
            <p className="text-gray-500">Adjust your filters or wait for new signups</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Source</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Tags</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Signup Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contacts.map((contact, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{contact.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{contact.name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {contact.source?.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {contact.tags?.map((tag, i) => (
                        <span key={i} className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded mr-1">
                          {tag}
                        </span>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        contact.email_status === 'active' 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {contact.email_status}
                      </span>
                      {contact.marketing_consent && (
                        <span className="ml-2 text-green-600 text-xs">✓ Consent</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {contact.created_at ? new Date(contact.created_at).toLocaleDateString('en-GB') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MailingListDashboard;
